import Vue from 'vue';
import IColors from '../models/colors';
import IRussiaDay, { IRussiaIsolatedHistory, IRussiaInfectedHistory, IRussiaRegion } from '../models/russia';
import Axios from 'axios';
import { IUser } from './store.user';

interface State {
	regionId: number;
	twoWeekData: boolean;
	twoWeekDataLoading: boolean;
	summary: IRussiaDay;
	isolated: IRussiaIsolatedHistory;
	infected: IRussiaInfectedHistory;
	arrived: IRussiaInfectedHistory;
	colors: IColors;
	datesWithData: Date[];
	regime: ({ title: string, valueYes: number, valueNo: number });
}
interface IModule {
	state: State;
	mutations: any;
	actions: any;
	getters: any;
	namespaced?: boolean;
}


const russia: IModule = {
	namespaced: true,
	state: {
		summary: {
			date: new Date(),
			arrivedTotal: 0,
			arrivedTwoWeekTotal: 0,
			arrivedPrevTwoWeekTotal: 0,
			isolatedNewTotal: 0,
			isolatedTwoWeek: 0,
			isolatedPrevTwoWeek: 0,
			infectedTotal: 0,
			isolatedNoTotal: 0,
			isolatedTotal: 0,
			isolatedYesTotal: 0,
			dateString: '',
			regions: [{
				code: 0,
				arrivedTwoWeek: 0,
				arrivedPrevTwoWeek: 0,
				arrivedCount: 0,
				infectedCount: 0,
				isolatedCount: 0,
				isolatedNo: 0,
				isolatedYes: 0,
				isolatedNew: 0,
				isolatedTwoWeek: 0,
				isolatedPrevTwoWeek: 0,
				name: '',
			}],
		},
		isolated: {
			regionsHistories: [{
				code: 0,
				histories: [{
					x: new Date(),
					y: 0,
					no: 0,
					yes: 0,
				}],
			}],
			totalHistories: [{
				x: new Date(),
				y: 0,
				no: 0,
				yes: 0,
			}],
		},
		infected: {
			regionsHistories: [{
				code: 0,
				histories: [{
					x: new Date(),
					y: 0,
				}],
			}],
			totalHistories: [{
				x: new Date(),
				y: 0,
			}],
		},
		arrived: {
			regionsHistories: [{
				code: 0,
				histories: [{
					x: new Date(),
					y: 0,
				}],
			}],
			totalHistories: [{
				x: new Date(),
				y: 0,
			}],
		},
		regionId: -1,
		twoWeekData: false,
		twoWeekDataLoading: false,
		colors: {
			nodata: '#f8f8f8',
			default: '#cfe8fc',
			hover: '#0000b3',
			border: '#808080',
		},
		datesWithData: [new Date()],
		regime: {
			title: '',
			valueNo: 0,
			valueYes: 0,
		},
	},
	mutations: {
		setSummary(state: State, summary: IRussiaDay) {
			if (!summary.regions.find((v) => v.code === state.regionId)) {
				state.regionId = -1;
			}
			if (summary.dateString) {
				const oldDate = state.summary.date;
				state.summary = summary;
				state.summary.date = oldDate;
			} else {
				state.summary = summary;
			}
		},
		setIsolated(state: State, isolated: IRussiaIsolatedHistory) {
			state.isolated = isolated;
		},
		setInfected(state: State, infected: IRussiaInfectedHistory) {
			state.infected = infected;
		},
		setArrived(state: State, arrived: IRussiaInfectedHistory) {
			state.arrived = arrived;
		},
		setRegionId(state: State, regionId: number) {
			state.regionId = regionId;
		},
		setColors(state: State, colors: IColors) {
			state.colors = colors;
			state.colors.border = state.colors.border.toLowerCase();
			state.colors.default = state.colors.default.toLowerCase();
			state.colors.hover = state.colors.hover.toLowerCase();
			state.colors.nodata = state.colors.nodata.toLowerCase();
		},
		setTwoWeekData(state: State, twoWeekData: boolean) {
			state.twoWeekData = twoWeekData;
		},
		setTwoWeekDataLoading(state: State, twoWeekDataLoading: boolean) {
			state.twoWeekDataLoading = twoWeekDataLoading;
		},
		setAllowedDates(state: State, dates: Date[]) {
			state.datesWithData = dates;
		},
		setRegime(state: State, regime) {
			state.regime = regime;
		},
	},
	actions: {
		updateSummary(context, summary: IRussiaDay) {
			context.commit('setSummary', summary);
		},
		updateIsolated(context, isolated: IRussiaIsolatedHistory) {
			context.commit('setIsolated', isolated);
		},
		updateInfected(context, infected: IRussiaInfectedHistory) {
			context.commit('setInfected', infected);
		},
		updateArrived(context, arrived: IRussiaInfectedHistory) {
			context.commit('setArrived', arrived);
		},
		updateRegionId(context, regionId: number) {
			const user = context.rootGetters['user/getUser'] as IUser;
			const isRegionUser = user.claims.some((v) => v.type === 'region');
			if (regionId === -1 && isRegionUser) {
				const userRegion = user.claims.find((v) => v.type === 'region')!.value;
				context.commit('setRegionId', +userRegion);
			} else {
				context.commit('setRegionId', regionId);
			}
		},
		updateColors(context, colors: IColors) {
			context.commit('setColors', colors);
		},
		async updateTwoWeekData(context, twoWeekData: boolean) {
			const endpoint = twoWeekData ? 'summaryTotal' : 'summary';
			context.commit('setTwoWeekData', twoWeekData);
			context.commit('setTwoWeekDataLoading', true);
			const response = await Axios.get(`/api/russia/${endpoint}`);
			context.commit('setTwoWeekDataLoading', false);
			context.dispatch('updateSummary', response.data.dayInfo);
		},
		updateAllowedDates(context, dates: Date[]) {
			context.commit('setAllowedDates', dates);
		},
		updateRegime(context, regime) {
			context.commit('setRegime', regime);
		},
	},
	getters: {
		getSummary: (state: State) => {
			const maxDateTime = new Date(state.summary.date);
			const maxDate = new Date(maxDateTime.getFullYear(), maxDateTime.getMonth(), maxDateTime.getDate()).valueOf();
			if (state.regionId === -1) {

				return {
					arrived: state.summary.arrivedTotal,
					arrivedTwoWeek: state.summary.arrivedTwoWeekTotal,
					arrivedPrevTwoWeek: state.summary.arrivedPrevTwoWeekTotal,
					isolatedTwoWeek: state.summary.isolatedTwoWeek,
					isolatedPrevTwoWeek: state.summary.isolatedPrevTwoWeek,
					isolated: state.summary.isolatedTotal,
					isolatedNew: state.summary.isolatedNewTotal,
					isolatedYes: state.summary.isolatedYesTotal,
					isolatedNo: state.summary.isolatedNoTotal,
				};
			} else {

				const region = state.summary.regions
					.find((r) => r.code === state.regionId);

				return {
					arrived: region?.arrivedCount,
					arrivedTwoWeek: region?.arrivedTwoWeek,
					arrivedPrevTwoWeek: region?.arrivedPrevTwoWeek,
					isolatedTwoWeek: region?.isolatedTwoWeek,
					isolatedPrevTwoWeek: region?.isolatedPrevTwoWeek,
					isolated: region?.isolatedCount,
					isolatedNew: region?.isolatedNew,
					isolatedYes: region?.isolatedYes,
					isolatedNo: region?.isolatedNo,
				};
			}
		},
		getRegions: (state: State, getters, rootState, rootGetters) => {
			const user = rootGetters['user/getUser'] as IUser;
			if (user.claims.some((v) => v.type === 'region')) {
				return state.summary.regions;
			} else {
				return [{ code: -1, name: 'РФ' } as IRussiaRegion, ...state.summary.regions];
			}
		},
		getRegionId: (state: State) => state.regionId,
		getIsolated: (state: State) => {
			if (state.regionId === -1) {
				return state.isolated.totalHistories;
			} else {
				const region = state.isolated.regionsHistories.find((r) => r.code === state.regionId);
				if (region) {
					return region.histories;
				} else {
					return state.isolated.totalHistories;
				}
			}
		},
		getArrived: (state: State) => {
			if (state.regionId === -1) {
				return state.arrived.totalHistories;
			} else {
				const region = state.arrived.regionsHistories.find((r) => r.code === state.regionId);
				if (region) {
					return region.histories;
				} else {
					return state.arrived.totalHistories;
				}
			}
		},
		getInfected: (state: State) => {
			if (state.regionId === -1) {
				return state.infected.totalHistories;
			} else {
				const region = state.infected.regionsHistories.find((r) => r.code === state.regionId);
				if (region) {
					return region.histories;
				} else {
					return state.infected.totalHistories;
				}
			}
		},
		getColors: (state: State) => state.colors,
		getRegime: (state: State) => {
			if (state.regionId === -1) {
				return [state.summary.isolatedYesTotal, state.summary.isolatedNoTotal];
			} else {
				const region = state.summary.regions.find((r) => r.code === state.regionId);
				if (region) {
					return [region.isolatedYes, region.isolatedNo];
				} else {
					return [state.summary.isolatedYesTotal, state.summary.isolatedNoTotal];
				}
			}
		},
		getLastUpdate: (state: State) => {
			if (state.summary.dateString && state.summary.dateString !== null) {
				return state.summary.dateString;
			} else {
				return new Date(state.summary.date).toLocaleDateString();
			}
		},
		getTotalCount: (state: State) => state.summary.arrivedTotal,
		getTwoWeekData: (state: State) => state.twoWeekData,
		getTwoWeekDataLoading: (state: State) => state.twoWeekDataLoading,
		getDataDate: (state: State) => state.summary.date,
		getAllowedDates: (state: State) => state.datesWithData,
		getRegimeFixed: (state: State) => ({
			title: state.regime.title,
			values: [state.regime.valueYes, state.regime.valueNo],
		}),
	},
};
export default russia;
